import { Button, Heading, Modal } from '@zillow/constellation-10';
import { useState } from 'react';
import useModal from '../../../hooks/useModal';
import { useConversationMessagesQuery } from '../../../hooks/useConversationMessagesQuery';
import type { ConversationMessagesData } from '../../../utils/conversationMessageUtils';
import type { OnContactsPageConversationNoteUpdate } from '../../../utils/contactsPageConversationsQueryParams';
import Notepad from '../../Notepad';

export const selectRenterName = (data: ConversationMessagesData): string => data.renterName || data.renterPhone || '';

type NotesModalProps = {
  clientTimeZone: string;
  conversationId: string;
  listingAlias: string;
  onContactsPageConversationNoteUpdate?: OnContactsPageConversationNoteUpdate;
  shouldRefetchStatusLabels: boolean;
};

export default function NotesModal({
  clientTimeZone,
  conversationId,
  listingAlias,
  onContactsPageConversationNoteUpdate,
  shouldRefetchStatusLabels,
}: NotesModalProps): JSX.Element | null {
  // Cache hit on the inbox; on the contacts / lead-management surfaces this dedupes
  // into the same single getMessages fetch the child Notepad issues (shared query key).
  const { data: renterName = '' } = useConversationMessagesQuery({
    conversationId,
    clientTimeZone,
    fetchIfCacheEmpty: true,
    select: selectRenterName,
  });

  const { clearModal } = useModal();
  const [resetAllInlineFeedbackChildState, setResetAllInlineFeedbackChildState] = useState(false);
  const [shouldSaveNoteText, setShouldSaveNoteText] = useState(false);

  const handleResetAllInlineFeedbackChildStateComplete = () => {
    setResetAllInlineFeedbackChildState(false);
  };

  const handleNoteSavingComplete = () => {
    setShouldSaveNoteText(false);
  };

  const handleOnClose = (): void => {
    setShouldSaveNoteText(true);
    clearModal();
  };

  const handleOnDoneClick = async (): Promise<void> => {
    setResetAllInlineFeedbackChildState(true);
    handleOnClose();
  };

  return (
    <Modal
      open={true}
      onOpenChange={(open) => !open && handleOnClose()}
      size={{
        base: 'fullScreen',
        md: 'xs',
      }}
      header={
        <Heading level={2} textStyle="heading-xs" css={{ textTransform: 'capitalize' }}>
          {renterName} Notes
        </Heading>
      }
      body={
        <Notepad
          clientTimeZone={clientTimeZone}
          conversationId={conversationId}
          listingAlias={listingAlias}
          onContactsPageConversationNoteUpdate={onContactsPageConversationNoteUpdate}
          shouldRefetchStatusLabels={shouldRefetchStatusLabels}
          resetAllInlineFeedbackChildState={resetAllInlineFeedbackChildState}
          onResetAllInlineFeedbackChildState={handleResetAllInlineFeedbackChildStateComplete}
          shouldSaveNoteText={shouldSaveNoteText}
          onHandleNoteSavingComplete={handleNoteSavingComplete}
        />
      }
      footer={
        <Button emphasis="filled" css={{ width: '100%' }} onClick={handleOnDoneClick} tone="brand">
          Done
        </Button>
      }
    />
  );
}
